// Which persona is this person, and how senior are they?
//
// ─────────────────────────────────────────────────────────────────────────────
// BECAUSE APOLLO'S FREE SEARCH ANSWERS NEITHER.
//
// `mixed_people/api_search` costs nothing and returns exactly this per person:
//
//   id, first_name, last_name_obfuscated, title, last_refreshed_at,
//   has_email, has_city, has_state, has_country, has_direct_phone, organization
//
// No `seniority`. No `departments`. The client mapped both anyway, so every
// staged candidate carried NULL in columns the screens sort and filter on, and
// `matched_persona` was hardcoded null on top of that. A batch of ten accounts
// produced 118 people, 0 with a seniority and 0 matched to a persona — a list
// long enough to be useless and flat enough to be unreadable.
//
// The title IS returned, and a title is what a persona is actually made of.
// Kyle's five personas are lists of titles: "CRO", "Chief Revenue Officer",
// "VP Marketing". So both answers are derivable from the one field we get, for
// free, with no second API call.
//
// ─────────────────────────────────────────────────────────────────────────────
// IT MATCHES ON WORDS, NOT ON A MODEL.
//
// This could be an LLM call and should not be. It runs once per candidate — 118
// on a ten-account run — so a model call here is per-record spend on a job that
// string matching does correctly, and a wrong guess would quietly point Kyle at
// the wrong entry point. Deterministic, auditable, and the operator can read the
// rule off the persona they wrote.
//
// WHAT IT REFUSES TO DO. It never invents a persona. A title that matches
// nothing returns null and the person still appears in the list, unmatched,
// where a human can look at them. Filtering them out would hide the CTO of an
// account because Kyle happened not to write "CTO" in a persona last month.

/** Coarsest first: the ranking used to sort a committee. */
export const SENIORITY_ORDER = [
  'owner', 'founder', 'c_suite', 'partner', 'vp', 'head', 'director',
  'manager', 'senior', 'entry', 'other',
] as const;
export type Seniority = (typeof SENIORITY_ORDER)[number];

/**
 * Seniority from a job title.
 *
 * ORDER MATTERS AND IS THE WHOLE FUNCTION. "VP of Operations, Founder" is a
 * founder; "Senior Director" is a director, not "senior"; "Head of Sales" is a
 * head even though "Sales" appears in a dozen other titles. Each test below is
 * placed so the more senior reading wins, because putting somebody one rung too
 * low buries them in a list of 118 and putting them one rung too high is a
 * cheap mistake to spot.
 */
export function seniorityFromTitle(title: string | null | undefined): Seniority {
  // Dots out FIRST. "C.E.O." and "S.V.P." are ordinary on LinkedIn-sourced
  // titles, and a word boundary treats the dots as separators — so the C-title
  // pattern never fires and a chief executive lands in "other", at the bottom
  // of a 118-row list. Caught by the test below.
  const t = String(title ?? '').toLowerCase().replace(/\./g, '').trim();
  if (!t) return 'other';

  // Ownership beats office. A "Founder & VP of Product" is reached as a founder.
  if (/\b(owner|proprietor)\b/.test(t)) return 'owner';
  if (/\b(founder|co-?founder|cofounder)\b/.test(t)) return 'founder';

  // A C-title, however it is written. `\bc[a-z]{1,2}o\b` catches CEO/CTO/CRO/
  // CMO/COO/CFO/CIO/CPO and their neighbours without listing them all.
  if (/\bchief\b/.test(t) || /\bc[a-z]{1,2}o\b/.test(t)) return 'c_suite';
  // "President" is a C-seat; "Vice President" is not, and the negative
  // lookbehind is the only thing keeping every VP out of the C-suite.
  if (/(?<!vice\s)(?<!\bvp\s)\bpresident\b/.test(t)) return 'c_suite';

  if (/\b(managing partner|general partner|partner)\b/.test(t)) return 'partner';
  if (/\b(vice president|vp|svp|evp|avp)\b/.test(t)) return 'vp';
  if (/\bhead of\b|\bglobal head\b/.test(t)) return 'head';
  if (/\bdirector\b/.test(t)) return 'director';
  if (/\bmanager\b|\blead\b/.test(t)) return 'manager';
  if (/\b(senior|sr\.?|principal|staff)\b/.test(t)) return 'senior';
  return 'other';
}

/** Sortable rank. Lower is more senior. Unknown sorts last, never first. */
export function seniorityRank(s: Seniority | string | null | undefined): number {
  const i = SENIORITY_ORDER.indexOf(String(s ?? '') as Seniority);
  return i === -1 ? SENIORITY_ORDER.length : i;
}

export interface PersonaRule {
  id?: string;
  name: string;
  titles?: unknown;
  seniorities?: unknown;
}

const words = (v: unknown): string[] =>
  (Array.isArray(v) ? v : []).map((x) => String(x).toLowerCase().trim()).filter(Boolean);

/** Words that carry no meaning in a job title and only get in the way. */
const FILLER = new Set(['of', 'the', 'and', 'for']);

/**
 * The same rank written two ways.
 *
 * Sensera's VP of Sales is the Revenue Chief seat and matched NOTHING, because
 * the persona says "VP of Sales" and Apollo returned "Vice President of Sales".
 * Whole-word comparison is right — it is what stops "President" claiming a vice
 * president — but it also means `vp` and `vice president` never meet. The fold
 * happens on BOTH sides before comparing, so a persona written either way finds
 * a title written either way.
 *
 * Multi-word keys are applied first: "senior vice president" must become `svp`,
 * not `senior vp`, or it stops matching a persona that says SVP.
 */
const SYNONYM_PAIRS: [string, string][] = [
  ['executive vice president', 'evp'],
  ['senior vice president', 'svp'],
  ['assistant vice president', 'avp'],
  ['vice president', 'vp'],
  ['chief executive officer', 'ceo'],
  ['chief revenue officer', 'cro'],
  ['chief marketing officer', 'cmo'],
  ['chief operating officer', 'coo'],
  ['chief financial officer', 'cfo'],
  ['chief technology officer', 'cto'],
  ['chief commercial officer', 'cco'],
  ['chief product officer', 'cpo'],
  ['sr', 'senior'],
  ['mgr', 'manager'],
  ['dir', 'director'],
  ['ops', 'operations'],
];

/**
 * THE WORD BOUNDARIES ARE BUILT, NOT TYPED.
 *
 * Written as regex literals the short entries are a corruption waiting to
 * happen: with no boundary, `dir` rewrites the word "director" as
 * "directorector" and `ops` mangles "devops". Constructing the pattern here
 * means the anchors cannot be lost to an editor, a codemod, or a shell that
 * eats backslashes - which is exactly how they were lost once already while
 * this function was being written.
 */
const SYNONYMS: [RegExp, string][] = SYNONYM_PAIRS.map(
  ([from, to]) => [new RegExp(`\\b${from}\\b`, 'g'), to],
);

/**
 * Both sides of a comparison, reduced to comparable words.
 *
 * FILLER IS DROPPED PER TOKEN, NOT BY PATTERN. A regex doing the same job has
 * to be word-boundary anchored, or "for" matches inside "Northeast" and
 * "VP of Sales, Northeast" becomes "vp sales nor east" - which then fails to
 * meet the persona that says "VP Sales". Splitting first makes that class of
 * bug impossible rather than merely guarded against, and it is how
 * hasWordRun compares below anyway.
 */
const NORMALISE = (v: string) => {
  let t = String(v).toLowerCase().replace(/\./g, '').replace(/[,&()]/g, ' ');
  // Synonyms BEFORE the token split, because several of them are multi-word.
  for (const [re, to] of SYNONYMS) t = t.replace(re, to);
  return t.split(/\s+/).filter((w) => w && !FILLER.has(w)).join(' ');
};

/**
 * Does `needle` appear in `hay` as a whole run of words?
 *
 * SUBSTRING MATCHING WAS WRONG AND CONFIDENTLY SO. A persona listing
 * "President" matched "Vice President of Finance Systems & Operations", so a
 * finance VP was reported as The Operating CEO. One listing "Principal" matched
 * "Principal Product Manager, Growth" and made a PM into a private-equity
 * sponsor. Both read as facts on the screen.
 */
function hasWordRun(hay: string, needle: string): boolean {
  const h = hay.split(' ');
  const n = needle.split(' ');
  if (!n.length || n.length > h.length) return false;
  for (let i = 0; i + n.length <= h.length; i++) {
    let hit = true;
    for (let j = 0; j < n.length; j++) if (h[i + j] !== n[j]) { hit = false; break; }
    if (!hit) continue;
    // "president" must not be satisfied by the tail of "vice president".
    if (n[0] === 'president' && i > 0 && h[i - 1] === 'vice') continue;
    return true;
  }
  return false;
}

/**
 * Which persona this title belongs to, or null.
 *
 * WHOLE WORDS, LONGEST MATCH, AND NO GUESSING BEYOND THAT.
 *
 * An earlier version fell back to the seniority band when no title matched —
 * "this is a vp, and some persona lists vp, so call it that". On the real batch
 * that put "Head of Enterprise Sales" and "VP of People Experience" into The
 * Marketing Seat, purely because it was the first persona listing those bands.
 * Forty-nine of a hundred and twenty-two were assigned that way.
 *
 * A wrong persona is worse than none. It decides which door Kyle knocks on, it
 * looks exactly as confident as a right one, and nothing downstream ever
 * questions it. So an unmatched person returns null, stays in the list, and
 * keeps their seniority — which is enough to work them by hand.
 */
export function matchPersona(
  title: string | null | undefined,
  personas: PersonaRule[],
): string | null {
  const hay = NORMALISE(String(title ?? ''));
  if (!hay || !personas.length) return null;

  let best: { name: string; len: number } | null = null;
  for (const p of personas) {
    for (const raw of words(p.titles)) {
      const needle = NORMALISE(raw);
      // Longest wins: "vp marketing" beats a bare "marketing" some other
      // persona happened to list.
      if (needle && hasWordRun(hay, needle) && (!best || needle.length > best.len)) {
        best = { name: p.name, len: needle.length };
      }
    }
  }
  return best ? best.name : null;
}
